import React, { useState, useEffect } from 'react';
import { X, Clock, User, Building2, Calculator, Plus, Trash2, GripVertical } from 'lucide-react';
import { Timesheet } from '../../types/collaborators';
import { collaboratorsService, projectsService, supabase } from '../../services/supabaseService';
import { usePermissions } from '../../hooks/usePermissions';

interface Task {
  id?: string;
  title: string;
  description: string;
  hours: number;
  orderIndex: number;
}

interface TimesheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (timesheet: Partial<Timesheet> & { tasks?: Task[] }) => void;
  timesheet?: Timesheet | null;
}

const TimesheetModal: React.FC<TimesheetModalProps> = ({ isOpen, onClose, onSave, timesheet }) => {
  const { isCollaboradorProgramador, isAdmin, isSuperAdmin } = usePermissions();
  const canApprove = (isAdmin() || isSuperAdmin()) && !!timesheet;
  const isProgrammerView = isCollaboradorProgramador();
  const [formData, setFormData] = useState({
    userId: '',
    collaboratorId: '',
    collaboratorName: '',
    projectId: '',
    projectName: '',
    date: '',
    hours: 0,
    appliedRate: 0,
    calculatedAmount: 0,
    description: '',
    status: 'draft' as Timesheet['status']
  });

  // Empezamos SIN task por defecto. Antes creaba una vacía y luego se
  // validaba como obligatoria, bloqueando al admin que sólo quería
  // cambiar el estado del parte a 'aprobado'.
  const [tasks, setTasks] = useState<Task[]>([]);

  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadDropdownData();
      if (isProgrammerView && !timesheet) {
        loadCurrentUserAsCollaborator();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (timesheet) {
      setFormData({
        userId: timesheet.userId || timesheet.collaboratorId,
        collaboratorId: timesheet.collaboratorId,
        collaboratorName: timesheet.collaboratorName,
        projectId: timesheet.projectId,
        projectName: timesheet.projectName,
        date: timesheet.date.toISOString().split('T')[0],
        hours: timesheet.hours,
        appliedRate: timesheet.appliedRate,
        calculatedAmount: timesheet.calculatedAmount,
        description: timesheet.description || '',
        status: timesheet.status
      });
    } else {
      setFormData({
        userId: '',
        collaboratorId: '',
        collaboratorName: '',
        projectId: '',
        projectName: '',
        date: new Date().toISOString().split('T')[0],
        hours: 0,
        appliedRate: 0,
        calculatedAmount: 0,
        description: '',
        status: 'draft'
      });
      // Al crear un nuevo parte sí arrancamos con una task vacía porque
      // el flujo estándar es que las horas se compongan de tareas.
      setTasks([{ title: '', description: '', hours: 0, orderIndex: 0 }]);
    }
  }, [timesheet]);

  // Recalcular horas totales SÓLO si hay tareas rellenadas. Si no hay,
  // respetar el valor manual de formData.hours (el parte puede tener
  // horas totales sin desglose por tarea).
  useEffect(() => {
    const validTasks = tasks.filter(t => t.title.trim());
    if (validTasks.length === 0) {
      // Recalcula solo el importe según horas actuales y tarifa.
      setFormData(prev => ({
        ...prev,
        calculatedAmount: (prev.hours || 0) * (prev.appliedRate || 0),
      }));
      return;
    }
    const totalHours = validTasks.reduce((sum, task) => sum + (task.hours || 0), 0);
    setFormData(prev => ({
      ...prev,
      hours: totalHours,
      calculatedAmount: totalHours * (prev.appliedRate || 0),
    }));
  }, [tasks, formData.appliedRate]);

  const loadCurrentUserAsCollaborator = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userData } = await supabase
        .from('users')
        .select('id, name, hourly_rate')
        .eq('id', user.id)
        .single();

      if (userData) {
        console.log('✅ Auto-cargando colaborador programador:', userData.name);
        setFormData(prev => ({
          ...prev,
          userId: userData.id,
          collaboratorId: userData.id,
          collaboratorName: userData.name,
          appliedRate: userData.hourly_rate || 0
        }));
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const loadDropdownData = async () => {
    try {
      setLoadingData(true);
      console.log('🔄 Cargando colaboradores y proyectos para dropdown...');

      const [collaboratorsData, projectsData] = await Promise.all([
        collaboratorsService.getAll(),
        projectsService.getAll()
      ]);

      console.log('✅ Plantilla cargados:', collaboratorsData.length);
      console.log('✅ Proyectos cargados:', projectsData.length);

      setCollaborators(collaboratorsData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      alert('Error al cargar colaboradores y proyectos');
    } finally {
      setLoadingData(false);
    }
  };

  const handleCollaboratorChange = (collaboratorId: string) => {
    const selectedCollaborator = collaborators.find(c => c.id === collaboratorId);
    if (selectedCollaborator) {
      setFormData(prev => ({
        ...prev,
        userId: collaboratorId,
        collaboratorId,
        collaboratorName: selectedCollaborator.name,
        appliedRate: selectedCollaborator.hourlyRate
      }));
    }
  };

  const handleProjectChange = (projectId: string) => {
    const selectedProject = projects.find(p => p.id === projectId);
    if (selectedProject) {
      setFormData(prev => ({
        ...prev,
        projectId,
        projectName: selectedProject.name
      }));
    }
  };

  const handleAddTask = () => {
    setTasks(prev => [
      ...prev,
      { title: '', description: '', hours: 0, orderIndex: prev.length }
    ]);
  };

  const handleRemoveTask = (index: number) => {
    if (tasks.length > 1) {
      setTasks(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleTaskChange = (index: number, field: keyof Task, value: string | number) => {
    setTasks(prev => prev.map((task, i) =>
      i === index ? { ...task, [field]: value } : task
    ));
  };

  const handleMoveTask = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === tasks.length - 1)
    ) {
      return;
    }

    const newTasks = [...tasks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newTasks[index], newTasks[targetIndex]] = [newTasks[targetIndex], newTasks[index]];

    newTasks.forEach((task, i) => {
      task.orderIndex = i;
    });

    setTasks(newTasks);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Sólo enviamos las tareas rellenadas. Las vacías se descartan
    // silenciosamente para no bloquear al admin que quiera aprobar/editar
    // el estado sin tocar tareas.
    const validTasks = tasks
      .filter(t => t.title.trim())
      .map((task, index) => ({ ...task, orderIndex: index }));

    onSave({
      ...formData,
      date: new Date(formData.date),
      tasks: validTasks,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {timesheet ? 'Editar Parte de Horas' : 'Nuevo Parte de Horas'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>Información del Parte</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!isProgrammerView && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Colaborador *
                  </label>
                  <select
                    required
                    value={formData.collaboratorId}
                    onChange={(e) => handleCollaboratorChange(e.target.value)}
                    disabled={loadingData}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">{loadingData ? 'Cargando colaboradores...' : 'Seleccionar colaborador'}</option>
                    {collaborators.map(collaborator => (
                      <option key={collaborator.id} value={collaborator.id}>
                        {collaborator.name} ({collaborator.hourlyRate}€/h)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className={isProgrammerView ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Proyecto *
                </label>
                <select
                  required
                  value={formData.projectId}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  disabled={loadingData}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{loadingData ? 'Cargando proyectos...' : 'Seleccionar proyecto'}</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {!loadingData && projects.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    No hay proyectos disponibles. Crea uno en la pestaña "Proyectos".
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Timesheet['status'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="draft">Borrador</option>
                  <option value="submitted">Enviado</option>
                  <option value="approved">Aprobado</option>
                  <option value="rejected">Rechazado</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tasks Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                <Calculator className="w-5 h-5" />
                <span>Tareas Realizadas</span>
              </h3>
              <button
                type="button"
                onClick={handleAddTask}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Agregar Tarea</span>
              </button>
            </div>

            <div className="space-y-4">
              {tasks.map((task, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start space-x-3">
                    <div className="flex flex-col space-y-1 pt-2">
                      <button
                        type="button"
                        onClick={() => handleMoveTask(index, 'up')}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <GripVertical className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nombre de la tarea
                          </label>
                          <input
                            type="text"
                            value={task.title}
                            onChange={(e) => handleTaskChange(index, 'title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="ej: Desarrollo de módulo de login"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Horas
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.25"
                            value={task.hours}
                            onChange={(e) => handleTaskChange(index, 'hours', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="2.5"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Descripción
                        </label>
                        <textarea
                          rows={2}
                          value={task.description}
                          onChange={(e) => handleTaskChange(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Describe los detalles de esta tarea..."
                        />
                      </div>
                    </div>

                    {tasks.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTask(index)}
                        className="text-red-600 hover:text-red-700 p-2 mt-6"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">
                  Total Horas
                </label>
                <div className="text-2xl font-bold text-blue-700">
                  {formData.hours.toFixed(2)}h
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">
                  Tarifa (€/h)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.appliedRate}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    appliedRate: parseFloat(e.target.value) || 0
                  }))}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  placeholder="45.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">
                  Importe Total
                </label>
                <div className="text-2xl font-bold text-blue-700">
                  {formData.calculatedAmount.toFixed(2)}€
                </div>
              </div>
            </div>
          </div>

          {/* General Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas generales (opcional)
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Notas adicionales sobre este parte de horas..."
            />
          </div>

          {/* Information Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-amber-900">Información importante</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Las horas totales se calculan automáticamente sumando las horas de todas las tareas.
                  Los partes aprobados se pueden incluir en las facturas del colaborador.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-200 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <div className="flex flex-wrap items-center gap-3">
              {canApprove && formData.status !== 'approved' && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, status: 'approved' });
                    // Enviar con status='approved' de inmediato.
                    onSave({
                      ...formData,
                      status: 'approved',
                      date: new Date(formData.date),
                      tasks: tasks.filter(t => t.title.trim()).map((task, i) => ({ ...task, orderIndex: i })),
                    });
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold"
                >
                  ✓ Aprobar parte
                </button>
              )}
              {canApprove && formData.status !== 'rejected' && (
                <button
                  type="button"
                  onClick={() => {
                    const reason = window.prompt('Motivo del rechazo (opcional):') ?? '';
                    setFormData({ ...formData, status: 'rejected' });
                    onSave({
                      ...formData,
                      status: 'rejected',
                      description: reason
                        ? `${formData.description}\n\n[Rechazo]: ${reason}`.trim()
                        : formData.description,
                      date: new Date(formData.date),
                      tasks: tasks.filter(t => t.title.trim()).map((task, i) => ({ ...task, orderIndex: i })),
                    });
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                >
                  ✕ Rechazar
                </button>
              )}
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {timesheet ? 'Actualizar' : 'Crear'} Parte
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimesheetModal;
